import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';
import { FormProvider } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import RequestSolutionIllustration from '@/assets/svg/request-solution-illustration.svg?react';
import RequestSolutionRequestSent from '@/assets/svg/request-solution-request-sent.svg?react';
import { Loader } from '@/components/loader/loader';
import { useBlockUnload } from '@/hooks/use-block-unload';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { useBlockNavigation } from '@/routes/hooks/use-block-navigation';
import { IconButton } from '@/ui/button/icon-button';
import { Stack } from '@/ui/line/line';
import { Header, ParagraphLarge } from '@/ui/typography/widgets';

import { RequestProjectForm } from './request-project-form';
import { useRequestProjectForm } from './use-request-project-form';
import { colors } from '@/theme/colors';

export const MainRequestProjectForm = () => {
  const { t } = useTranslation('request-project');
  const location = useLocation();
  const { methods, handleRequestProjectSubmit, isSubmitting, isSuccess } =
    useRequestProjectForm();
  const {
    formState: { isDirty },
  } = methods;
  const navigateTo = useNavigateTo();

  const fromLink = location.state?.fromLink === true;

  const ref = useRef<HTMLDivElement>(null);

  useBlockUnload(isDirty);
  useBlockNavigation({ when: isDirty });

  const handleBack = useCallback(() => {
    window.history.back();
  }, []);

  const handleCancel = useCallback(() => {
    navigateTo.home();
  }, [navigateTo]);

  const handleSubmit = useCallback(() => {
    void handleRequestProjectSubmit();
  }, [handleRequestProjectSubmit]);

  return (
    <Container>
      <FormProvider {...methods}>
        <PageContainer>
          <ContentGrid>
            <LeftSection $hideBackButton={fromLink}>
              {!fromLink && (
                <BackButtonContainer>
                  <IconButton
                    icon="ArrowLeft2"
                    variant="ghost"
                    size="16px"
                    onClick={handleBack}
                    color={colors.gray.c12}
                    dataTestId="request-project-back-button"
                  />
                  <BackText onClick={handleBack}>{t`actions.back`}</BackText>
                </BackButtonContainer>
              )}

              <ContentSection>
                <HeaderSection>
                  <PageTitle>{t`title`}</PageTitle>
                  <Subtitle>{t`subtitle`}</Subtitle>
                </HeaderSection>
                <IllustrationContainer>
                  <RequestSolutionIllustration
                    style={{ transform: 'scale(1.5)' }}
                  />
                </IllustrationContainer>
              </ContentSection>
            </LeftSection>

            <FormCardContainer>
              <FormCard data-testid="request-project-form-card">
                {isSubmitting ? (
                  <Loader />
                ) : (
                  <FormContent gap="32px" ref={ref}>
                    {isSuccess ? (
                      <SuccessContent data-testid="request-project-success-content">
                        <SuccessIllustration data-testid="request-project-success-illustration">
                          <RequestSolutionRequestSent
                            style={{ transform: 'scale(1.5)' }}
                          />
                        </SuccessIllustration>
                        <SuccessTitle>{t`success.title`}</SuccessTitle>
                        <SuccessMessage>{t`success.message`}</SuccessMessage>
                      </SuccessContent>
                    ) : (
                      <>
                        <FormHeader>
                          <FormTitle>{t`title`}</FormTitle>
                          <FormSubtitle>{t`subtitle`}</FormSubtitle>
                        </FormHeader>

                        <RequestProjectForm
                          onCancel={handleCancel}
                          onSubmit={handleSubmit}
                          submitButtonLabel={t`actions.submit`}
                        />
                      </>
                    )}
                  </FormContent>
                )}
              </FormCard>
            </FormCardContainer>
          </ContentGrid>
        </PageContainer>
      </FormProvider>
    </Container>
  );
};

const Container = styled('div')`
  position: relative;
`;

const PageContainer = styled('div')`
  width: 100%;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.gray.c1};
  padding: 0;
`;

const ContentGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 600px;
  min-height: 100vh;
  padding: 0 54px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const LeftSection = styled('div')<{ $hideBackButton?: boolean }>`
  background-color: ${({ theme }) => theme.colors.gray.c1};
  padding: ${({ $hideBackButton }) =>
    $hideBackButton ? '136px 0 40px 40px' : '40px 0 40px 40px'};
  display: flex;
  flex-direction: column;

  @media (max-width: 1200px) {
    padding: ${({ $hideBackButton }) =>
      $hideBackButton ? '120px 24px 24px' : '24px'};
  }
`;

const BackButtonContainer = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 60px;
  width: fit-content;
`;

const BackText = styled('span')`
  color: ${({ theme }) => theme.colors.gray.c12};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.gray.c11};
  }
`;

const ContentSection = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 720px;
  padding: 0 40px;

  @media (max-width: 1200px) {
    justify-content: flex-start;
    margin-bottom: 40px;
  }
`;

const HeaderSection = styled('div')`
  margin-bottom: 40px;
`;

const PageTitle = styled(Header)`
  color: ${({ theme }) => theme.colors.basics.black};
  margin-bottom: 24px;
  font-size: 32px;
  font-weight: 600;
  line-height: 1.25;
`;

const Subtitle = styled(ParagraphLarge)`
  color: ${({ theme }) => theme.colors.gray.c12};
  line-height: 1.6;
  font-size: 16px;
  white-space: pre-line;
`;

const IllustrationContainer = styled('div')`
  margin-top: 60px;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  svg {
    max-width: 100%;
    height: auto;
    max-height: 300px;
  }

  padding: 42px;

  @media (max-width: 1200px) {
    display: none;
  }
`;

const FormCardContainer = styled('div')`
  background-color: ${({ theme }) => theme.colors.gray.c1};
  padding: 60px 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 1200px) {
    padding: 40px 24px;
    align-items: flex-start;
    padding-top: 60px;
  }
`;

const FormCard = styled('div')`
  background-color: ${({ theme }) => theme.colors.basics.white};
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.shadow.formCard};
  padding: 48px 40px;
  width: 100%;
  height: 100%;
  max-width: 520px;
  border: 1px solid ${({ theme }) => theme.colors.gray.c3};
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    padding: 32px 24px;
    border-radius: 12px;
    margin: 0 16px;
  }
`;

const FormContent = styled(Stack)`
  width: 100%;
`;

const FormHeader = styled('div')`
  text-align: center;
  margin-bottom: 8px;
  font-family: ${({ theme }) => theme.fonts.neue};

  @media (min-width: 1200px) {
    display: none;
  }
`;

const FormTitle = styled('h1')`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.basics.black};
  margin: 0 0 12px 0;
  line-height: 1.3;
`;

const FormSubtitle = styled('p')`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray.c12};
  margin: 0;
  line-height: 1.5;
  white-space: pre-line;
`;

const SuccessContent = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;
  max-width: 300px;
  margin: 0 auto;
  width: 100%;
`;

const SuccessIllustration = styled('div')`
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const SuccessTitle = styled('h2')`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.basics.black};
  margin: 0;
  line-height: 1.3;
`;

const SuccessMessage = styled('p')`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.gray.c12};
  margin: 0;
  line-height: 1.5;
  text-align: center;
`;
